/**
 * StageModeSwitcher — 3-mode toggle chip (AI · Import · Manual).
 *
 * Every 8-stage pipeline surface renders one of these at the top so the
 * user can choose whether parameters come from:
 *   · AI      — Claude Sonnet 4.5 analyses the imported data and suggests
 *               values with reasoning.
 *   · Import  — Values read directly from a CSV/file the user uploaded.
 *   · Manual  — User types every value by hand.
 *
 * The active mode is persisted in localStorage (per project, per stage)
 * so it survives navigation and refresh.
 */
import { useEffect, useState, useCallback } from "react";
import { Sparkles, Upload, PencilRuler } from "lucide-react";

const MODES = [
  { id: "ai",     label: "AI",     icon: Sparkles,
    hint: "AI reads your data and suggests parameters with reasoning." },
  { id: "import", label: "Import", icon: Upload,
    hint: "Read parameters directly from an uploaded CSV / file." },
  { id: "manual", label: "Manual", icon: PencilRuler,
    hint: "Type every value by hand." },
];

const storageKey = (projectId, stageId) => `smin.stage-mode.${projectId || "_"}.${stageId}`;

export function useStageMode(projectId, stageId, defaultMode = "ai") {
  const [mode, setModeRaw] = useState(() => {
    if (typeof window === "undefined") return defaultMode;
    try {
      return window.localStorage.getItem(storageKey(projectId, stageId)) || defaultMode;
    } catch {
      return defaultMode;
    }
  });
  const setMode = useCallback((next) => {
    setModeRaw(next);
    try { window.localStorage.setItem(storageKey(projectId, stageId), next); } catch { /* ignore */ }
  }, [projectId, stageId]);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem(storageKey(projectId, stageId));
      if (v && v !== mode) setModeRaw(v);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, stageId]);
  return [mode, setMode];
}

export default function StageModeSwitcher({
  mode,
  onChange,
  hideModes,
  testidPrefix = "stage-mode",
}) {
  const visible = MODES.filter((m) => !hideModes?.includes(m.id));
  const active = MODES.find((m) => m.id === mode) || visible[0];
  return (
    <div className="flex items-center gap-3" data-testid={`${testidPrefix}-switcher`}>
      <div className="inline-flex items-center gap-1 p-1 rounded-full border border-neutral-200 bg-neutral-50">
        {visible.map((m) => {
          const Icon = m.icon;
          const isActive = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange?.(m.id)}
              data-testid={`${testidPrefix}-${m.id}`}
              className={`inline-flex items-center gap-1.5 h-7 px-3 text-[11.5px] font-medium rounded-full transition ${
                isActive
                  ? "bg-[#4CAF50] text-white shadow-sm"
                  : "text-neutral-600 hover:text-neutral-900"
              }`}
              title={m.hint}
              aria-pressed={isActive}
            >
              <Icon className="h-3.5 w-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>
      {active && (
        <span className="text-[11px] text-neutral-500 hidden sm:inline-block">
          {active.hint}
        </span>
      )}
    </div>
  );
}
